/* eslint-disable no-console */
import { atom } from 'jotai';
import { atomWithReset, atomWithDefault, atomWithStorage } from 'jotai/utils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { loadWebAppsData } from './helper';
import { WAFilter } from '../config';
import { waRefreshAtom } from '../state';

export const forceRerenderUIAtom = atom<string>('');

export const pageSizeAtom = atomWithReset(DEFAULT_PAGE_SIZE);
export const currentPageAtom = atomWithReset(1);
export const orderbyAtom = atomWithReset<string>('vuln');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const orderingAtom = atom(async (get) => {
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  return `${order === 'desc' ? '-' : ''}${orderBy}`;
});

export const selectedWebAppsFilterAtom = atomWithReset<WAFilter>(
  WAFilter.ACTIVE,
);
export const searchPhraseFilterAtom = atomWithReset<string>('');

export const DEFAULT_ACTIVE_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'vuln',
  'webapp_created_date',
  'last_scan_date',
  'results_status',
  'auth_status',
  'actions',
];
export const activeWAColumnKeysAtom = atomWithStorage(
  'wa_list_active_column_keys',
  DEFAULT_ACTIVE_WA_LISTING_COLUMNS,
);

export const DEFAULT_DEACTIVED_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'vuln',
  'last_scan_date',
  'results_status',
  'auth_status',
  'deactivated_by',
  'last_deactivated_at',
  'actions',
];
export const deactivatedWAColumnKeysAtom = atomWithStorage(
  'wa_list_deactivated_column_keys',
  DEFAULT_DEACTIVED_WA_LISTING_COLUMNS,
);

export const DEFAULT_AGED_OUT_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'deleted_webapp__open_vuln_counts',
  'deleted_webapp__closed_vuln_counts',
  'deactivated_by',
  'last_deactivated_at',
  'deleted_webapp__deleted_by',
  'deleted_webapp__deleted_at',
  'actions',
];
export const agedOutWAColumnKeysAtom = atomWithStorage(
  'wa_list_aged_out_column_keys',
  DEFAULT_AGED_OUT_WA_LISTING_COLUMNS,
);

export const paginationConfigAtom = atomWithReset({
  page_size: DEFAULT_PAGE_SIZE,
  page: 1,
});

export const checkedListAtom = atomWithReset<number[]>([]);
export const isCheckAllAtom = atomWithReset(false);

// component height
export const componentHeightAtom = atom(0);

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsVulnMapAtom = atom<Record<number, number>>({});

export const vulnCountForCheckedHostsAtom = atom(async (get) => {
  const checkedHostIds = get(checkedListAtom);
  const vulnMap = get(checkedHostsVulnMapAtom);
  let total = 0;

  if (checkedHostIds?.length > 0) {
    const allHosts = get(waListingDataAtom);

    // Update vulnMap with any new hosts from current page
    const currentPageHosts = allHosts?.items?.filter((host) =>
      checkedHostIds.includes(host.id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored value if available, otherwise 0
      total += vulnMap[hostId] || 0;
    });

    // Update totals with any new values from current page
    currentPageHosts?.forEach((host) => {
      if (host.vuln !== vulnMap[host.id]) {
        total = total - (vulnMap[host.id] || 0) + (host.vuln || 0);
      }
    });
  }
  return total;
});

export const waListingDataAtom = atomWithDefault(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const currentPage = paginationConfig.page;

  const ordering = get(orderingAtom);
  const selectedWebAppsFilter = get(selectedWebAppsFilterAtom);
  const searchPhraseFilter = get(searchPhraseFilterAtom);

  const _ = get(waRefreshAtom);

  try {
    const responseData = await loadWebAppsData(
      companyId,
      pageSize,
      currentPage,
      ordering,
      searchPhraseFilter,
      selectedWebAppsFilter,
    );
    return {
      items: responseData.items,
      count: responseData.count,
    };
  } catch (error) {
    console.error(error);
  }

  return {
    items: undefined,
    count: 0,
  };
});
